import dynamic from 'next/dynamic';

import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

// Utils
import useToggle from '@/utils/hooks/utils/useToggle';

import { PopoverContainer, PopoverWrapper } from '@/components/base/Popover';
// Components
import TabsPopover from '@/components/base/Tabs/TabsPopover';
import { Footer } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/Footer';
import { GroupsTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab';
import { ProfilesTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab';
import { RecapSocialProfiles } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/RecapSocialProfiles';
import { TooltipPlanRestrictionContent } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
const SaveSelectedProfilesAsGroupModal = dynamic(() =>
    import('@/components/composite/SpGroups/SaveSelectedProfilesAsGroupModal').then(
        (mod) => mod.SaveSelectedProfilesAsGroupModal
    )
);

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

import type { TabsPopoverProps } from '@/components/base/Tabs/TabsPopover';

export function ManageProfiles() {
    const { t } = useTranslation('layout_platform');
    const { activeGroup } = useActiveGroup();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { hasGroupPermission } = useUserGroupRestriction();
    const { isOpen, toggle, params, setParams } = useToggle<SocialProfileType[]>();

    const nbProfiles = activatedSocialProfiles?.length || 0;
    const nbGroups = activatedSpGroups?.length || 0;

    const tabs: TabsPopoverProps['tabs'] = [
        {
            label: t('manage_profiles.tabs.all_profiles', { count: nbProfiles }),
            icon: {
                active: 'single-profile-on',
                inactive: 'single-profile'
            }
        },
        {
            label: t('manage_profiles.tabs.groups', { count: nbGroups }),
            icon: {
                active: 'group-on',
                inactive: 'group'
            },
            disabled: !hasGroupPermission,
            tooltip: {
                appendTo: document.body,
                interactive: true,
                disabled: hasGroupPermission,
                content: <TooltipPlanRestrictionContent />
            }
        }
    ];

    const handleSaveSelectedProfiles = (profileIds: SocialProfileType['id'][]) => {
        setParams(profileIds.map((id) => activatedSocialProfiles.find((sp) => sp.id === id)));
        toggle();
    };

    return (
        <>
            <PopoverWrapper
                popperOptions={{
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['auto']
                            }
                        }
                    ]
                }}>
                {({ open, setReferenceElement, setPopperElement, styles, attributes }) => (
                    <>
                        <RecapSocialProfiles setReferenceElement={setReferenceElement} />
                        <PopoverContainer
                            open={open}
                            setPopperElement={setPopperElement}
                            styles={styles}
                            attributes={attributes}>
                            <TabsPopover defaultIndex={Number(Boolean(activeGroup))} tabs={tabs}>
                                <ProfilesTab
                                    handleSaveSelectedProfiles={handleSaveSelectedProfiles}
                                />
                                <GroupsTab />
                            </TabsPopover>
                            <Footer />
                        </PopoverContainer>
                    </>
                )}
            </PopoverWrapper>
            <SaveSelectedProfilesAsGroupModal
                isOpen={isOpen}
                toggle={toggle}
                selectedProfiles={params}
            />
        </>
    );
}
